import apiConfig, { API_GROUP_LIST_URL } from 'config/api';
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { concurrentRequestsResolver, makeRequestQuery, preflightAPICall, processAPIError, translate, setListPage } from 'utils';
import axios, { AxiosError } from 'axios';
import { UserList } from './list';
import { TError, TListData, TStandardSectionStep } from 'models';
import { TBroker, TDrawerType } from '../common';
import Drawer from './drawer';
import { setListPageSize } from 'app/Pages/common/List';
import { BrokerProfile } from './profile';

export const API_BROKER_LIST_URL = `${apiConfig.MASTER}/brokers`;
const API_BROKER_ITEM_URL = `${apiConfig.MASTER}/brokers/%BROKER_ID%`;
const API_BROKER_CREATE = `${apiConfig.MASTER}/brokers`;

export type TTableData = TListData & { results?: TBroker[] };

interface IProps {
  selectedBroker: TBroker | null,
  updateSelectedBroker: (user: TBroker | null) => void,
  drawerType: TDrawerType,
  handleDrawerClose: (page?: number) => void,
  updateStep: (href: string) => void,
  handleAddUserClick: () => void,
  handleUpdateSelectedBroker: (data: TBroker | null) => void,
}

const PageAdminBroker = ({
  selectedBroker,
  updateSelectedBroker,
  drawerType,
  handleDrawerClose,
  updateStep,
  handleAddUserClick,
  handleUpdateSelectedBroker
}: IProps) => {
  const location = useLocation();
  let history = useHistory();

  const [dataStatus, updateDataStatus] = useState('loading');
  const [brokerList, updateBrokerList] = useState<TTableData>({});
  const [searchQuery, updateSearchQuery] = useState<string>();
  const [brokerErrors, updateBrokerErrors] = useState<TError[]>([]);
  const [page, updatePage] = useState<number | undefined>();
  const [noResultsText, updateNoResultsText] = useState('');

  const {
    section,
    subsection,
    itemId,
  } = useParams<{ section: TStandardSectionStep, subsection: string, itemId: string }>();

  useEffect(() => {
    if (section === 'list' && page) {
      getList();
    } else if (section === 'profile' && !selectedBroker && itemId) {
      getBroker(itemId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, page]);

  useEffect(() => {
    if (typeof searchQuery !== 'undefined') {
      history.replace(`${location.pathname}`);

      if (!page || page === 1) {
        getList();
      } else {
        updatePage(1);
      }
    } else {
      setListPage(page, updatePage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const getList = () => {
    updateDataStatus('loading');
    let query: Record<string, string | number> = {};

    query.page_size = setListPageSize();

    if (page) {
      query.page = page;
    }

    if (searchQuery) {
      query.search = encodeURIComponent(searchQuery);
    }

    const queryString = makeRequestQuery(query);

    preflightAPICall(() => {
      axios.get(`${API_BROKER_LIST_URL}${queryString}`).then(response => {
        const data = response.data;
        dataLoading(data);

      }).catch(error => {

        dataLoading({
          results: [],
        });

        updateNoResultsText(() => processAPIError(error) as string);

      });
    });
  };

  const getBroker = (itemId: string) => {
    preflightAPICall(() => {
      axios.get(API_BROKER_ITEM_URL.replace('%BROKER_ID%', itemId)).then(response => {
        const data = response.data;
        updateSelectedBroker(data);
        updateDataStatus('success');
      }).catch(error => {
        updateDataStatus('error');
        updateBrokerErrors(() => processAPIError(error, true) as TError[]);
      });
    });
  };

  const dataLoading = (data: TTableData) => {
    data = {
      ...data,
      results: data.results
    };
    updateBrokerList(data);
    updateDataStatus('success');
  };

  const handlePostBroker = (requestObject: TBroker, onError: (error: AxiosError) => void) => {
    preflightAPICall(() => {
      axios.post(API_BROKER_CREATE, {
        name: requestObject.name,
        tag: requestObject.tag
      }).then(response => {
        const data = response.data;
        //Creating roles
        createRoles(data.group_id, () => {

          let results = brokerList.results ? [data, ...brokerList.results] : [data];
          handleDrawerClose();
          updateBrokerList({
            ...brokerList,
            results: results
          });
        }, onError);

      }).catch(onError);
    });
  };

  const handleDeleteUser = (brokerId?: string, onError?: (error: AxiosError) => void) => {
    brokerId = typeof brokerId === 'string' ? brokerId : selectedBroker?.id;
    if (brokerId) {
      preflightAPICall(() => {
        axios.delete(API_BROKER_ITEM_URL.replace('%BROKER_ID%', brokerId as string)).then(() => {
          onBrokerDeleted(brokerId as string);
        }).catch(error => {
          if (onError) {
            onError(error);
          } else {
            console.error(error);
          }
        });
      });
    }
  };

  const onBrokerDeleted = (translationId: string) => {
    const list = brokerList.results?.length ? [...brokerList.results] : [];

    if (list?.length) {
      const customerIndex = list.findIndex(i => i.id === translationId);
      if (customerIndex !== -1) {
        list.splice(customerIndex, 1);
      }
    }

    updateStep(`list?page=${page}`);
    updateSelectedBroker(null);
    updateBrokerList({
      ...brokerList,
      results: list,
    });
  };

  const handlePatchBroker = (broker: TBroker, onError?: (error: AxiosError) => void, onSuccess?: () => void, shouldDrawerClose?: boolean) => {
    preflightAPICall(() => {
      axios.patch(API_BROKER_ITEM_URL.replace('%BROKER_ID%', broker.id), {
        ...broker
      }).then(response => {
        const data = response.data;
        const results = brokerList.results?.length ? [...brokerList.results] : [];
        const itemIndex = results.findIndex(i => i.id === data.id);

        if (itemIndex !== -1) {
          results[itemIndex] = data;
        }

        if (!shouldDrawerClose) {
          handleDrawerClose();
        }

        updateBrokerList({
          ...brokerList,
          results: results
        });
        updateSelectedBroker(data);

        if (onSuccess) {
          onSuccess();
        }

      }).catch(error => {
        if (onError) {
          onError(error);
        } else {
          console.error(error);
        }
      });
    });
  };

  const handleSearchChange = (value: string) => updateSearchQuery(value);

  const handleListChangePage = (page: number) => {
    history.replace(`/broker/list?page=${page}`);
    updatePage(page);
  };

  return (
    <>
      <UserList
        step={section}
        title={translate({ key: 'master.broker.list_title' })}
        dataStatus={dataStatus}
        brokerList={brokerList}
        updateSelectedUser={handleUpdateSelectedBroker}
        selectedUser={selectedBroker}
        drawerType={drawerType}
        noResultsText={noResultsText}
        handleSearchChange={handleSearchChange}
        handleListChangePage={handleListChangePage}
        handleAddUserClick={handleAddUserClick}
      />
      <BrokerProfile
        step={section}
        updateStep={updateStep}
        activeTab={subsection}
        brokerId={itemId}
        profile={selectedBroker}
        dataStatus={dataStatus}
        errors={brokerErrors}
        handlePatchBroker={handlePatchBroker}
      />
      <Drawer
        type={drawerType}
        selectedUser={selectedBroker}
        brokerId={itemId}
        handleDrawerClose={() => handleDrawerClose(page)}
        updateStep={updateStep}
        step={section}
        handlePatchUser={handlePatchBroker}
        handleDeleteUser={handleDeleteUser}
        handlePostUser={handlePostBroker}
        updateSelectedUser={updateSelectedBroker}
      />
    </>
  );
};

export const createRoles = (parentId: string, onSuccess: () => void, onError: (error: AxiosError) => void) => {
  preflightAPICall(() => {
    const dataRequests = [
      axios.post(`${API_GROUP_LIST_URL}/${parentId}/roles`, {
        name: 'manager'
      }),
      axios.post(`${API_GROUP_LIST_URL}/${parentId}/roles`, {
        name: 'agent'
      })
    ];

    const dataRequestsResolved = concurrentRequestsResolver(dataRequests);

    if (dataRequestsResolved.length > 0) {
      axios.all(dataRequestsResolved).then(axios.spread((manager, agent) => {
        // @ts-ignore need to investigate how to handle such different types of response
        if (manager.error) {
          const response = manager as { error: Error };
          onError(response.error as AxiosError);

          // @ts-ignore need to investigate how to handle such different types of response
        } else if (agent.error) {
          const response = agent as { error: Error };
          onError(response.error as AxiosError);

          // @ts-ignore need to investigate how to handle such different types of response
        } else {
          onSuccess();
        }

      })).catch(onError);
    }
  });
};

export default PageAdminBroker;
